import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  CubeIcon,
  CalendarDaysIcon,
  DocumentDuplicateIcon,
  BellAlertIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  PlayIcon,
  PauseIcon,
  EyeIcon,
  UserGroupIcon,
  ArrowPathIcon,
  ChartBarIcon,
  AcademicCapIcon,
  ClockIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  BellIcon,
  InboxIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'
import {
  // Types
  type BillableService,
  type ServiceEnrollment,
  type InvoiceSchedule,
  type ReminderConfig,
  type BatchInvoiceJob,
  type BillingStats,
  type PreviewResult,
  type ServiceCategory,
  type BillingFrequency,
  type ScheduleFrequency,
  type TargetType,
  type ReminderType,
  type ReminderChannel,
  // API functions
  getServices,
  createService,
  updateService,
  deleteService,
  getEnrollments,
  enrollStudents,
  unenrollStudent,
  getSchedules,
  createSchedule,
  updateSchedule,
  toggleScheduleStatus,
  deleteSchedule,
  previewBatchInvoices,
  generateBatchInvoices,
  getBatchJobs,
  getReminderConfigs,
  createReminderConfig,
  updateReminderConfig,
  deleteReminderConfig,
  getBillingStats,
  // Helpers
  SERVICE_CATEGORIES,
  BILLING_FREQUENCIES,
  SCHEDULE_FREQUENCIES,
  TARGET_TYPES,
  REMINDER_TYPES,
  REMINDER_CHANNELS,
  getCategoryColor,
  getCategoryLabel,
  getFrequencyLabel,
  getBatchStatusColor,
  formatCurrency,
} from '@/api/billingApi'
import { getGrades } from '@/api/academicApi'
import { getUsers } from '@/api/usersApi'

// ============================================================================
// TYPES & SCHEMAS
// ============================================================================

const serviceSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  category: z.enum(['TUITION', 'TRANSPORT', 'MEALS', 'BOARDING', 'UNIFORM', 'BOOKS', 'ACTIVITIES', 'EXAM_FEES', 'LAB_FEES', 'LIBRARY', 'TECHNOLOGY', 'INSURANCE', 'OTHER']),
  billingFrequency: z.enum(['ONE_TIME', 'DAILY', 'WEEKLY', 'MONTHLY', 'TERMLY', 'YEARLY']),
  baseAmount: z.number().min(0, 'Amount must be positive'),
  taxRate: z.number().min(0).max(100).default(0),
  discountable: z.boolean().default(true),
  refundable: z.boolean().default(false),
  optional: z.boolean().default(true),
})

const scheduleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  frequency: z.enum(['ONCE', 'DAILY', 'WEEKLY', 'MONTHLY', 'TERMLY', 'YEARLY']),
  targetType: z.enum(['ALL_STUDENTS', 'GRADE', 'STREAM', 'CUSTOM']),
  nextRunDate: z.string().min(1, 'Next run date is required'),
  dueOffsetDays: z.number().min(0).default(30),
  autoSend: z.boolean().default(false),
  notifyParents: z.boolean().default(true),
})

const reminderSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  reminderType: z.enum(['BEFORE_DUE', 'ON_DUE', 'OVERDUE', 'ESCALATION']),
  daysOffset: z.number().min(0, 'Days offset must be positive'),
  frequency: z.enum(['ONCE', 'DAILY', 'WEEKLY']).default('ONCE'),
  maxReminders: z.number().min(1).max(10).default(3),
  emailTemplate: z.string().optional(),
  smsTemplate: z.string().optional(),
})

type ServiceFormData = z.infer<typeof serviceSchema>
type ScheduleFormData = z.infer<typeof scheduleSchema>
type ReminderFormData = z.infer<typeof reminderSchema>

// Tab configuration
const TABS = [
  { id: 'services', label: 'Services', icon: CubeIcon },
  { id: 'batch', label: 'Batch Generation', icon: DocumentDuplicateIcon },
  { id: 'schedules', label: 'Schedules', icon: CalendarDaysIcon },
  { id: 'reminders', label: 'Reminders', icon: BellAlertIcon },
] as const

type TabId = (typeof TABS)[number]['id']

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BillingManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('services')
  const [stats, setStats] = useState<BillingStats | null>(null)
  const [loading, setLoading] = useState(true)

  // Services state
  const [services, setServices] = useState<BillableService[]>([])
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [editingService, setEditingService] = useState<BillableService | null>(null)
  const [serviceSearch, setServiceSearch] = useState('')

  // Batch state
  const [batchJobs, setBatchJobs] = useState<BatchInvoiceJob[]>([])
  const [showBatchModal, setShowBatchModal] = useState(false)
  const [batchPreview, setBatchPreview] = useState<PreviewResult | null>(null)
  const [selectedServices, setSelectedServices] = useState<string[]>([])
  const [batchTargetType, setBatchTargetType] = useState<TargetType>('ALL_STUDENTS')
  const [batchTargetGrades, setBatchTargetGrades] = useState<string[]>([])
  const [batchDueDate, setBatchDueDate] = useState('')
  const [batchName, setBatchName] = useState('')
  const [batchAutoSend, setBatchAutoSend] = useState(false)

  // Schedules state
  const [schedules, setSchedules] = useState<InvoiceSchedule[]>([])
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<InvoiceSchedule | null>(null)
  const [scheduleServices, setScheduleServices] = useState<string[]>([])
  const [scheduleTargetGrades, setScheduleTargetGrades] = useState<string[]>([])

  // Reminders state
  const [reminderConfigs, setReminderConfigs] = useState<ReminderConfig[]>([])
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [editingReminder, setEditingReminder] = useState<ReminderConfig | null>(null)
  const [reminderChannels, setReminderChannels] = useState<ReminderChannel[]>(['EMAIL'])

  // Reference data
  const [grades, setGrades] = useState<Array<{ _id: string; name: string }>>([])
  const [students, setStudents] = useState<Array<{ _id: string; firstName: string; lastName: string }>>([])

  // Forms
  const serviceForm = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      name: '',
      description: '',
      category: 'TUITION',
      billingFrequency: 'TERMLY',
      baseAmount: 0,
      taxRate: 0,
      discountable: true,
      refundable: false,
      optional: true,
    },
  })

  const scheduleForm = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      name: '',
      description: '',
      frequency: 'MONTHLY',
      targetType: 'ALL_STUDENTS',
      nextRunDate: '',
      dueOffsetDays: 30,
      autoSend: false,
      notifyParents: true,
    },
  })

  const reminderForm = useForm<ReminderFormData>({
    resolver: zodResolver(reminderSchema),
    defaultValues: {
      name: '',
      reminderType: 'BEFORE_DUE',
      daysOffset: 7,
      frequency: 'ONCE',
      maxReminders: 3,
    },
  })

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  useEffect(() => {
    fetchStats()
    fetchGrades()
  }, [])

  useEffect(() => {
    if (activeTab === 'services') fetchServices()
    else if (activeTab === 'batch') {
      fetchServices()
      fetchBatchJobs()
    } else if (activeTab === 'schedules') {
      fetchServices()
      fetchSchedules()
    } else if (activeTab === 'reminders') fetchReminderConfigs()
  }, [activeTab])

  const fetchStats = async () => {
    try {
      const response = await getBillingStats()
      setStats(response.data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const fetchGrades = async () => {
    try {
      const response = await getGrades()
      setGrades(response.data || [])
    } catch (error) {
      console.error('Error fetching grades:', error)
    }
  }

  const fetchServices = async () => {
    setLoading(true)
    try {
      const response = await getServices({ search: serviceSearch || undefined })
      // Handle various response structures
      const servicesData = response?.data?.services || response?.services || response?.data || []
      setServices(Array.isArray(servicesData) ? servicesData : [])
    } catch (error) {
      console.error('Error fetching services:', error)
      toast.error('Failed to fetch services')
    } finally {
      setLoading(false)
    }
  }

  const fetchBatchJobs = async () => {
    try {
      const response = await getBatchJobs()
      setBatchJobs(response.data?.jobs || [])
    } catch (error) {
      console.error('Error fetching batch jobs:', error)
    }
  }

  const fetchSchedules = async () => {
    setLoading(true)
    try {
      const response = await getSchedules()
      setSchedules(response.data?.schedules || [])
    } catch (error) {
      console.error('Error fetching schedules:', error)
      toast.error('Failed to fetch schedules')
    } finally {
      setLoading(false)
    }
  }

  const fetchReminderConfigs = async () => {
    setLoading(true)
    try {
      const response = await getReminderConfigs()
      setReminderConfigs(response.data?.configs || [])
    } catch (error) {
      console.error('Error fetching reminder configs:', error)
      toast.error('Failed to fetch reminder configurations')
    } finally {
      setLoading(false)
    }
  }

  // ============================================================================
  // SERVICE HANDLERS
  // ============================================================================

  const handleCreateService = async (data: ServiceFormData) => {
    try {
      await createService({
        ...data,
        applicability: 'ALL',
      })
      toast.success('Service created successfully')
      setShowServiceModal(false)
      serviceForm.reset()
      fetchServices()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create service')
    }
  }

  const handleUpdateService = async (data: ServiceFormData) => {
    if (!editingService) return
    try {
      await updateService(editingService._id, data)
      toast.success('Service updated successfully')
      setShowServiceModal(false)
      setEditingService(null)
      serviceForm.reset()
      fetchServices()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update service')
    }
  }

  const handleDeleteService = async (service: BillableService) => {
    if (!confirm(`Are you sure you want to delete "${service.name}"?`)) return
    try {
      await deleteService(service._id)
      toast.success('Service deleted successfully')
      fetchServices()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete service')
    }
  }

  const openEditService = (service: BillableService) => {
    setEditingService(service)
    serviceForm.reset({
      name: service.name,
      description: service.description || '',
      category: service.category,
      billingFrequency: service.billingFrequency,
      baseAmount: service.baseAmount,
      taxRate: service.taxRate,
      discountable: service.discountable,
      refundable: service.refundable,
      optional: service.optional,
    })
    setShowServiceModal(true)
  }

  // ============================================================================
  // BATCH HANDLERS
  // ============================================================================

  const handlePreviewBatch = async () => {
    if (selectedServices.length === 0) {
      toast.error('Please select at least one service')
      return
    }
    try {
      const response = await previewBatchInvoices({
        targetType: batchTargetType,
        targetGrades: batchTargetType === 'GRADE' ? batchTargetGrades : undefined,
        serviceIds: selectedServices,
      })
      setBatchPreview(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to preview batch')
    }
  }

  const handleGenerateBatch = async () => {
    if (!batchName.trim()) {
      toast.error('Please enter a batch name')
      return
    }
    if (!batchDueDate) {
      toast.error('Please select a due date')
      return
    }
    if (selectedServices.length === 0) {
      toast.error('Please select at least one service')
      return
    }
    try {
      await generateBatchInvoices({
        name: batchName,
        targetType: batchTargetType,
        targetGrades: batchTargetType === 'GRADE' ? batchTargetGrades : undefined,
        serviceIds: selectedServices,
        dueDate: batchDueDate,
        autoSend: batchAutoSend,
      })
      toast.success('Batch invoice generation started')
      setShowBatchModal(false)
      resetBatchForm()
      fetchBatchJobs()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate batch invoices')
    }
  }

  const resetBatchForm = () => {
    setSelectedServices([])
    setBatchTargetType('ALL_STUDENTS')
    setBatchTargetGrades([])
    setBatchDueDate('')
    setBatchName('')
    setBatchAutoSend(false)
    setBatchPreview(null)
  }

  // ============================================================================
  // SCHEDULE HANDLERS
  // ============================================================================

  const handleCreateSchedule = async (data: ScheduleFormData) => {
    if (scheduleServices.length === 0) {
      toast.error('Please select at least one service')
      return
    }
    try {
      await createSchedule({
        ...data,
        serviceIds: scheduleServices,
        targetGrades: data.targetType === 'GRADE' ? scheduleTargetGrades : undefined,
      })
      toast.success('Schedule created successfully')
      setShowScheduleModal(false)
      scheduleForm.reset()
      setScheduleServices([])
      setScheduleTargetGrades([])
      fetchSchedules()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create schedule')
    }
  }

  const handleUpdateSchedule = async (data: ScheduleFormData) => {
    if (!editingSchedule) return
    try {
      await updateSchedule(editingSchedule._id, {
        ...data,
        serviceIds: scheduleServices,
        targetGrades: data.targetType === 'GRADE' ? scheduleTargetGrades : undefined,
      })
      toast.success('Schedule updated successfully')
      setShowScheduleModal(false)
      setEditingSchedule(null)
      scheduleForm.reset()
      setScheduleServices([])
      setScheduleTargetGrades([])
      fetchSchedules()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update schedule')
    }
  }

  const handleToggleSchedule = async (schedule: InvoiceSchedule) => {
    try {
      await toggleScheduleStatus(schedule._id)
      toast.success(`Schedule ${schedule.isActive ? 'paused' : 'activated'}`)
      fetchSchedules()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to toggle schedule')
    }
  }

  const handleDeleteSchedule = async (schedule: InvoiceSchedule) => {
    if (!confirm(`Are you sure you want to delete "${schedule.name}"?`)) return
    try {
      await deleteSchedule(schedule._id)
      toast.success('Schedule deleted successfully')
      fetchSchedules()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete schedule')
    }
  }

  const openEditSchedule = (schedule: InvoiceSchedule) => {
    setEditingSchedule(schedule)
    scheduleForm.reset({
      name: schedule.name,
      description: schedule.description || '',
      frequency: schedule.frequency,
      targetType: schedule.targetType,
      nextRunDate: new Date(schedule.nextRunDate).toISOString().split('T')[0],
      dueOffsetDays: schedule.dueOffsetDays,
      autoSend: schedule.autoSend,
      notifyParents: schedule.notifyParents,
    })
    setScheduleServices(typeof schedule.serviceIds[0] === 'string' ? schedule.serviceIds as string[] : (schedule.serviceIds as BillableService[]).map(s => s._id))
    setScheduleTargetGrades(schedule.targetGrades || [])
    setShowScheduleModal(true)
  }

  // ============================================================================
  // REMINDER HANDLERS
  // ============================================================================

  const handleCreateReminder = async (data: ReminderFormData) => {
    if (reminderChannels.length === 0) {
      toast.error('Please select at least one channel')
      return
    }
    try {
      await createReminderConfig({
        ...data,
        channels: reminderChannels,
      })
      toast.success('Reminder configuration created successfully')
      setShowReminderModal(false)
      reminderForm.reset()
      setReminderChannels(['EMAIL'])
      fetchReminderConfigs()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create reminder config')
    }
  }

  const handleUpdateReminder = async (data: ReminderFormData) => {
    if (!editingReminder) return
    try {
      await updateReminderConfig(editingReminder._id, {
        ...data,
        channels: reminderChannels,
      })
      toast.success('Reminder configuration updated successfully')
      setShowReminderModal(false)
      setEditingReminder(null)
      reminderForm.reset()
      setReminderChannels(['EMAIL'])
      fetchReminderConfigs()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update reminder config')
    }
  }

  const handleDeleteReminder = async (config: ReminderConfig) => {
    if (!confirm(`Are you sure you want to delete "${config.name}"?`)) return
    try {
      await deleteReminderConfig(config._id)
      toast.success('Reminder configuration deleted successfully')
      fetchReminderConfigs()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete reminder config')
    }
  }

  const openEditReminder = (config: ReminderConfig) => {
    setEditingReminder(config)
    reminderForm.reset({
      name: config.name,
      reminderType: config.reminderType,
      daysOffset: config.daysOffset,
      frequency: config.frequency,
      maxReminders: config.maxReminders,
      emailTemplate: config.emailTemplate || '',
      smsTemplate: config.smsTemplate || '',
    })
    setReminderChannels(config.channels)
    setShowReminderModal(true)
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing Management</h1>
          <p className="text-gray-500 mt-1">Manage services, batch invoicing, schedules, and reminders</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Services</p>
                <p className="text-2xl font-bold text-gray-900">{stats.services?.active ?? 0}</p>
                <p className="text-xs text-gray-400 mt-1">{stats.services?.total ?? 0} total</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <CubeIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Enrollments</p>
                <p className="text-2xl font-bold text-gray-900">{stats.enrollments?.active ?? 0}</p>
                <p className="text-xs text-gray-400 mt-1">{stats.enrollments?.total ?? 0} total</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <UserGroupIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Schedules</p>
                <p className="text-2xl font-bold text-gray-900">{stats.schedules?.active ?? 0}</p>
                <p className="text-xs text-gray-400 mt-1">{stats.schedules?.total ?? 0} total</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <CalendarDaysIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Invoices Generated</p>
                <p className="text-2xl font-bold text-gray-900">{stats.batchJobs?.totalInvoicesGenerated ?? 0}</p>
                <p className="text-xs text-gray-400 mt-1">{stats.batchJobs?.completed ?? 0} batch jobs</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <DocumentDuplicateIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchServices()}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <Button onClick={() => { setEditingService(null); serviceForm.reset(); setShowServiceModal(true); }}>
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add Service
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : services.length === 0 ? (
                <div className="text-center py-12">
                  <CubeIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No services found</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map((service) => (
                    <motion.div
                      key={service._id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900">{service.name}</h3>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${getCategoryColor(service.category)}-100 text-${getCategoryColor(service.category)}-700`}>
                            {getCategoryLabel(service.category)}
                          </span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${service.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {service.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {service.description && (
                        <p className="text-sm text-gray-500 mb-3 line-clamp-2">{service.description}</p>
                      )}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Amount:</span>
                          <span className="font-medium text-gray-900">{formatCurrency(service.baseAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Frequency:</span>
                          <span className="text-gray-700">{getFrequencyLabel(service.billingFrequency)}</span>
                        </div>
                        {service.taxRate > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Tax:</span>
                            <span className="text-gray-700">{service.taxRate}%</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                        <Button variant="outline" size="sm" onClick={() => openEditService(service)} className="flex-1">
                          <PencilIcon className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteService(service)} className="text-red-600 hover:bg-red-50">
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Batch Generation Tab */}
          {activeTab === 'batch' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Batch Invoice Generation</h3>
                <Button onClick={() => { resetBatchForm(); setShowBatchModal(true); }}>
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Generate Batch
                </Button>
              </div>

              {/* Recent Batch Jobs */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Batch Jobs</h4>
                {batchJobs.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
                    <DocumentDuplicateIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No batch jobs yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {batchJobs.slice(0, 5).map((job) => (
                      <div key={job._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{job.name}</p>
                          <p className="text-sm text-gray-500">
                            {job.successCount} invoices generated | Due: {new Date(job.dueDate).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium bg-${getBatchStatusColor(job.status)}-100 text-${getBatchStatusColor(job.status)}-700`}>
                            {job.status}
                          </span>
                          <span className="text-sm text-gray-500">{new Date(job.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Schedules Tab */}
          {activeTab === 'schedules' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Invoice Schedules</h3>
                <Button onClick={() => { setEditingSchedule(null); scheduleForm.reset(); setScheduleServices([]); setScheduleTargetGrades([]); setShowScheduleModal(true); }}>
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add Schedule
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : schedules.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarDaysIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No schedules configured</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {schedules.map((schedule) => (
                    <motion.div
                      key={schedule._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${schedule.isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                          <CalendarDaysIcon className={`w-6 h-6 ${schedule.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{schedule.name}</h4>
                          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                            <span>{getFrequencyLabel(schedule.frequency)}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                            <span>Next: {new Date(schedule.nextRunDate).toLocaleDateString()}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                            <span>{TARGET_TYPES[schedule.targetType]}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleSchedule(schedule)}
                          className={`p-2 rounded-lg transition-colors ${schedule.isActive ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}
                          title={schedule.isActive ? 'Pause' : 'Activate'}
                        >
                          {schedule.isActive ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => openEditSchedule(schedule)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteSchedule(schedule)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reminders Tab */}
          {activeTab === 'reminders' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Payment Reminders</h3>
                <Button onClick={() => { setEditingReminder(null); reminderForm.reset(); setReminderChannels(['EMAIL']); setShowReminderModal(true); }}>
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add Reminder
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : reminderConfigs.length === 0 ? (
                <div className="text-center py-12">
                  <BellAlertIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No reminder configurations</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {reminderConfigs.map((config) => (
                    <motion.div
                      key={config._id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-white border border-gray-200 rounded-xl p-5"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{config.name}</h4>
                          <span className="text-sm text-gray-500">{REMINDER_TYPES[config.reminderType].label}</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {config.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Days Offset:</span>
                          <span className="text-gray-700">{config.daysOffset} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Frequency:</span>
                          <span className="text-gray-700">{config.frequency}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Max Reminders:</span>
                          <span className="text-gray-700">{config.maxReminders}</span>
                        </div>
                        <div className="flex gap-2 mt-2">
                          {config.channels.map((channel) => (
                            <span key={channel} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                              {REMINDER_CHANNELS[channel].label}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                        <Button variant="outline" size="sm" onClick={() => openEditReminder(config)} className="flex-1">
                          <PencilIcon className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteReminder(config)} className="text-red-600 hover:bg-red-50">
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Service Modal */}
      <AnimatePresence>
        {showServiceModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowServiceModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingService ? 'Edit Service' : 'Add Service'}
                </h2>
                <button onClick={() => setShowServiceModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={serviceForm.handleSubmit(editingService ? handleUpdateService : handleCreateService)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
                  <Input {...serviceForm.register('name')} placeholder="e.g., Tuition Fee" />
                  {serviceForm.formState.errors.name && (
                    <p className="text-sm text-red-500 mt-1">{serviceForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    {...serviceForm.register('description')}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Optional description..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select
                      {...serviceForm.register('category')}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {Object.entries(SERVICE_CATEGORIES).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Billing Frequency *</label>
                    <select
                      {...serviceForm.register('billingFrequency')}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {Object.entries(BILLING_FREQUENCIES).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Amount (KES) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      {...serviceForm.register('baseAmount', { valueAsNumber: true })}
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                    <Input
                      type="number"
                      step="0.01"
                      {...serviceForm.register('taxRate', { valueAsNumber: true })}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...serviceForm.register('discountable')} className="rounded text-primary-600" />
                    <span className="text-sm text-gray-700">Discountable</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...serviceForm.register('refundable')} className="rounded text-primary-600" />
                    <span className="text-sm text-gray-700">Refundable</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...serviceForm.register('optional')} className="rounded text-primary-600" />
                    <span className="text-sm text-gray-700">Optional</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowServiceModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={serviceForm.formState.isSubmitting}>
                    {serviceForm.formState.isSubmitting ? 'Saving...' : (editingService ? 'Update' : 'Create')}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Batch Generation Modal */}
      <AnimatePresence>
        {showBatchModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowBatchModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Generate Batch Invoices</h2>
                <button onClick={() => setShowBatchModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch Name *</label>
                  <Input
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                    placeholder="e.g., Term 1 2025 School Fees"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Services *</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {services.filter(s => s.isActive).map((service) => (
                      <label key={service._id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedServices.includes(service._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedServices([...selectedServices, service._id])
                            } else {
                              setSelectedServices(selectedServices.filter(id => id !== service._id))
                            }
                          }}
                          className="rounded text-primary-600"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{service.name}</p>
                          <p className="text-sm text-gray-500">{formatCurrency(service.baseAmount)}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Students *</label>
                    <select
                      value={batchTargetType}
                      onChange={(e) => setBatchTargetType(e.target.value as TargetType)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {Object.entries(TARGET_TYPES).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                    <Input
                      type="date"
                      value={batchDueDate}
                      onChange={(e) => setBatchDueDate(e.target.value)}
                    />
                  </div>
                </div>

                {batchTargetType === 'GRADE' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Grades</label>
                    <div className="flex flex-wrap gap-2">
                      {grades.map((grade) => (
                        <label key={grade._id} className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={batchTargetGrades.includes(grade._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBatchTargetGrades([...batchTargetGrades, grade._id])
                              } else {
                                setBatchTargetGrades(batchTargetGrades.filter(id => id !== grade._id))
                              }
                            }}
                            className="rounded text-primary-600"
                          />
                          <span className="text-sm">{grade.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={batchAutoSend}
                    onChange={(e) => setBatchAutoSend(e.target.checked)}
                    className="rounded text-primary-600"
                  />
                  <span className="text-sm text-gray-700">Auto-send invoices to parents</span>
                </label>

                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={handlePreviewBatch}>
                    <EyeIcon className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  {batchPreview && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{batchPreview.totalStudents}</span> students |{' '}
                      <span className="font-medium">{formatCurrency(batchPreview.totalAmount)}</span> total
                    </div>
                  )}
                </div>

                {batchPreview && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Preview Summary</h4>
                    <div className="space-y-2 text-sm">
                      {batchPreview.services.map((service) => (
                        <div key={service.serviceId} className="flex justify-between">
                          <span className="text-gray-600">{service.serviceName} ({service.studentCount} students)</span>
                          <span className="font-medium">{formatCurrency(service.totalAmount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 border-t border-gray-200 font-medium">
                        <span>Total</span>
                        <span>{formatCurrency(batchPreview.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowBatchModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleGenerateBatch}>
                    <DocumentDuplicateIcon className="w-4 h-4 mr-2" />
                    Generate Invoices
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Schedule Modal */}
      <AnimatePresence>
        {showScheduleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowScheduleModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
                </h2>
                <button onClick={() => setShowScheduleModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={scheduleForm.handleSubmit(editingSchedule ? handleUpdateSchedule : handleCreateSchedule)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Name *</label>
                  <Input {...scheduleForm.register('name')} placeholder="e.g., Monthly Tuition" />
                  {scheduleForm.formState.errors.name && (
                    <p className="text-sm text-red-500 mt-1">{scheduleForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    {...scheduleForm.register('description')}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Optional description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Services *</label>
                  <div className="space-y-2 max-h-36 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {services.filter(s => s.isActive).map((service) => (
                      <label key={service._id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={scheduleServices.includes(service._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setScheduleServices([...scheduleServices, service._id])
                            } else {
                              setScheduleServices(scheduleServices.filter(id => id !== service._id))
                            }
                          }}
                          className="rounded text-primary-600"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{service.name}</p>
                          <p className="text-sm text-gray-500">{formatCurrency(service.baseAmount)}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Frequency *</label>
                    <select
                      {...scheduleForm.register('frequency')}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {Object.entries(SCHEDULE_FREQUENCIES).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Students *</label>
                    <select
                      {...scheduleForm.register('targetType')}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {Object.entries(TARGET_TYPES).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {scheduleForm.watch('targetType') === 'GRADE' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Grades</label>
                    <div className="flex flex-wrap gap-2">
                      {grades.map((grade) => (
                        <label key={grade._id} className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={scheduleTargetGrades.includes(grade._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setScheduleTargetGrades([...scheduleTargetGrades, grade._id])
                              } else {
                                setScheduleTargetGrades(scheduleTargetGrades.filter(id => id !== grade._id))
                              }
                            }}
                            className="rounded text-primary-600"
                          />
                          <span className="text-sm">{grade.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next Run Date *</label>
                    <Input type="date" {...scheduleForm.register('nextRunDate')} />
                    {scheduleForm.formState.errors.nextRunDate && (
                      <p className="text-sm text-red-500 mt-1">{scheduleForm.formState.errors.nextRunDate.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Offset (days)</label>
                    <Input
                      type="number"
                      {...scheduleForm.register('dueOffsetDays', { valueAsNumber: true })}
                      placeholder="30"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...scheduleForm.register('autoSend')} className="rounded text-primary-600" />
                    <span className="text-sm text-gray-700">Auto-send invoices</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...scheduleForm.register('notifyParents')} className="rounded text-primary-600" />
                    <span className="text-sm text-gray-700">Notify parents</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowScheduleModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={scheduleForm.formState.isSubmitting}>
                    {scheduleForm.formState.isSubmitting ? 'Saving...' : (editingSchedule ? 'Update' : 'Create')}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reminder Modal */}
      <AnimatePresence>
        {showReminderModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowReminderModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingReminder ? 'Edit Reminder' : 'Add Reminder'}
                </h2>
                <button onClick={() => setShowReminderModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={reminderForm.handleSubmit(editingReminder ? handleUpdateReminder : handleCreateReminder)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Name *</label>
                  <Input {...reminderForm.register('name')} placeholder="e.g., 7-day before due reminder" />
                  {reminderForm.formState.errors.name && (
                    <p className="text-sm text-red-500 mt-1">{reminderForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Type *</label>
                    <select
                      {...reminderForm.register('reminderType')}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {Object.entries(REMINDER_TYPES).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Days Offset *</label>
                    <Input
                      type="number"
                      {...reminderForm.register('daysOffset', { valueAsNumber: true })}
                      placeholder="7"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                    <select
                      {...reminderForm.register('frequency')}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="ONCE">Once</option>
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Reminders</label>
                    <Input
                      type="number"
                      {...reminderForm.register('maxReminders', { valueAsNumber: true })}
                      min="1"
                      max="10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notification Channels *</label>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(REMINDER_CHANNELS).map(([key, value]) => (
                      <label key={key} className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={reminderChannels.includes(key as ReminderChannel)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setReminderChannels([...reminderChannels, key as ReminderChannel])
                            } else {
                              setReminderChannels(reminderChannels.filter(c => c !== key))
                            }
                          }}
                          className="rounded text-primary-600"
                        />
                        <span className="text-sm">{value.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {reminderChannels.includes('EMAIL') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Template</label>
                    <textarea
                      {...reminderForm.register('emailTemplate')}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                      placeholder="Dear {parent_name}, this is a reminder that invoice {invoice_number} for {student_name} is due on {due_date}..."
                    />
                  </div>
                )}

                {reminderChannels.includes('SMS') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMS Template</label>
                    <textarea
                      {...reminderForm.register('smsTemplate')}
                      rows={2}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                      placeholder="Payment reminder: {amount} due for {student_name} on {due_date}. Ref: {invoice_number}"
                    />
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowReminderModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={reminderForm.formState.isSubmitting}>
                    {reminderForm.formState.isSubmitting ? 'Saving...' : (editingReminder ? 'Update' : 'Create')}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default BillingManagementPage
